import type { ChartCandle } from "@/app/api/live/chart/route";
import { reportError } from "@/lib/reportError";

export type ChartTimeRange = "1H" | "1D" | "7D" | "1M" | "YTD" | "1Y" | "5Y" | "ALL";

const DAY_MS = 24 * 60 * 60 * 1000;
const WS_CHART_TIMEOUT_MS = 20_000;

export function resolveSocketWsUrl(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  const candidates = [
    trimmed,
    trimmed.startsWith("ws://") || trimmed.startsWith("wss://") || trimmed.includes("://")
      ? null
      : `https://${trimmed}`,
  ].filter((v): v is string => Boolean(v));

  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      if (!parsed.host) continue;
      const wsProtocol = parsed.protocol === "https:" || parsed.protocol === "wss:" ? "wss:" : "ws:";
      return `${wsProtocol}//${parsed.host}/api/live/chart/ws`;
    } catch {
      continue;
    }
  }

  return null;
}

export function sortChartCandles(data: ChartCandle[]): ChartCandle[] {
  return [...data]
    .filter((c) => !c.is_gap)
    .sort(
      (a, b) =>
        new Date(a.candle_start).getTime() - new Date(b.candle_start).getTime(),
    );
}

/** Reject only clearly wrong payloads (e.g. 1D data landing while YTD is selected). */
export function chartDataMatchesRange(candles: ChartCandle[], range: ChartTimeRange): boolean {
  if (candles.length === 0) return true;

  const firstMs = new Date(candles[0].candle_start).getTime();
  const lastMs = new Date(candles[candles.length - 1].candle_start).getTime();
  if (!Number.isFinite(firstMs) || !Number.isFinite(lastMs) || lastMs < firstMs) {
    return false;
  }

  const spanMs = lastMs - firstMs;

  switch (range) {
    case "1H":
      return spanMs <= 4 * 60 * 60 * 1000;
    case "1D":
      return spanMs <= 4 * DAY_MS;
    case "7D":
      return spanMs >= 2 * DAY_MS && spanMs <= 12 * DAY_MS;
    case "1M":
      return spanMs >= 10 * DAY_MS && spanMs <= 50 * DAY_MS;
    case "YTD":
      return spanMs >= 2 * DAY_MS;
    case "1Y":
      return spanMs >= 30 * DAY_MS;
    case "5Y":
      return spanMs >= 90 * DAY_MS;
    case "ALL":
      return spanMs >= 30 * DAY_MS;
    default:
      return true;
  }
}

async function fetchChartCandlesHttp(range: ChartTimeRange): Promise<ChartCandle[]> {
  const res = await fetch(`/api/live/chart?range=${encodeURIComponent(range)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) throw new Error("Invalid chart payload");
  return data as ChartCandle[];
}

function fetchChartCandlesWebSocket(
  range: ChartTimeRange,
  timeoutMs = WS_CHART_TIMEOUT_MS,
): Promise<ChartCandle[]> {
  const wsUrl = resolveSocketWsUrl(process.env.NEXT_PUBLIC_ZCASH_SOCKET_URL ?? "");
  if (!wsUrl) return Promise.reject(new Error("No socket URL configured"));

  return new Promise((resolve, reject) => {
    let settled = false;
    const ws = new WebSocket(wsUrl);

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };

    const timer = setTimeout(() => {
      finish(() => {
        ws.close();
        reject(new Error("WebSocket chart timeout"));
      });
    }, timeoutMs);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "range", range }));
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as { type?: string; data?: unknown };
        if (msg.type !== "chart" || !Array.isArray(msg.data)) return;
        const sorted = sortChartCandles(msg.data as ChartCandle[]);
        if (!chartDataMatchesRange(sorted, range)) return;
        finish(() => {
          ws.close();
          resolve(sorted);
        });
      } catch {
        /* wait for next message */
      }
    };

    ws.onerror = () => {
      finish(() => reject(new Error("WebSocket error")));
    };

    ws.onclose = (event: CloseEvent) => {
      if (!settled) {
        finish(() => reject(new Error(`WebSocket closed (${event.code})`)));
      }
    };
  });
}

/** HTTP first; socket backend when Next.js cannot reach Binance (e.g. Vercel US). */
export async function fetchChartCandlesClient(range: ChartTimeRange): Promise<ChartCandle[]> {
  try {
    return await fetchChartCandlesHttp(range);
  } catch (httpErr) {
    try {
      return await fetchChartCandlesWebSocket(range);
    } catch (wsErr) {
      reportError(`[Chart] HTTP and WS fetch failed for ${range}:`, wsErr, {
        range,
        httpError: httpErr instanceof Error ? httpErr.message : String(httpErr),
      });
      throw wsErr;
    }
  }
}
